import { ExternalLink } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { DateCell } from '../../../../components/table/DateCell';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { TRAINING_RECOMMENDATION_STATUS_LABELS, type SurveyTrainingRecommendation } from '../../survey/surveyTypes';
import { trainingRecommendationsApi } from '../trainingRecommendationApi';

/**
 * Learning & Development › Training Recommendations — every development plan's training
 * recommendations, across every survey, in one register.
 *
 * - **Title** is the row's anchor and opens the recommendation's profile: Info, Approval, and the
 *   Personnel on its plan with each one's progress on it.
 * - **Development Plan** and **Survey** say where it was written — the two columns a plan's own
 *   Training Recommendations tab does not need.
 * - **Approval** is where it stands with its approver; **Sent** is when it reached the employees
 *   on its plan — approved is not the same as sent.
 *
 * NO Create and nothing here deletes: a recommendation is written on its plan, under Survey, on
 * Survey's keys. This module's one key reads.
 */
export function TrainingRecommendationsIndexPage() {
  const navigate = useNavigate();

  const fetchRows = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      trainingRecommendationsApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
        },
        signal,
      ),
    [],
  );

  const columns = useMemo<ColumnDef<SurveyTrainingRecommendation>[]>(
    () => [
      {
        key: 'title',
        header: 'Title',
        width: 320,
        flex: true,
        sortable: true,
        render: (row) => <TextCell value={row.title} />,
      },
      {
        key: 'planTitle',
        header: 'Development Plan',
        width: 260,
        sortable: true,
        summary: true,
        render: (row) => <TextCell value={row.planTitle} />,
      },
      {
        key: 'surveyTitle',
        header: 'Survey',
        width: 260,
        sortable: true,
        summary: true,
        render: (row) => <TextCell value={row.surveyTitle} />,
      },
      {
        // Blank until it is sent — approved is not the same as sent.
        key: 'sentAt',
        header: 'Sent',
        width: 170,
        render: (row) => <DateCell value={row.sentAt ?? null} />,
      },
      {
        // Where it stands with its approver — its lifecycle, not whether the row is live.
        // Keyed apart from `status`: that key is the row's LIVE state, which this is not.
        key: 'approvalStatus',
        header: 'Approval',
        width: 160,
        stage: true,
        render: (row) => <StatusText tone="inactive">{TRAINING_RECOMMENDATION_STATUS_LABELS[row.status]}</StatusText>,
      },
      {
        key: 'status',
        header: 'Status',
        width: 120,
        status: true,
        render: (row) =>
          row.isActive ? <StatusText tone="active">Active</StatusText> : <StatusText tone="inactive">Deleted</StatusText>,
      },
    ],
    [],
  );

  const rowActions = useCallback(
    (row: SurveyTrainingRecommendation): MenuItem[] => [
      {
        // Each entry carries the key of the page it LANDS on — another module's.
        key: `plan-${row.id}`,
        label: 'Open development plan',
        icon: <ExternalLink className="h-4 w-4" aria-hidden="true" />,
        permission: 'development-plans.read',
        onClick: () => navigate(`/learning-development/development-plan/${row.surveyPlanId}`),
      },
      {
        key: `survey-${row.id}`,
        label: 'Open survey',
        icon: <ExternalLink className="h-4 w-4" aria-hidden="true" />,
        permission: 'surveys.read',
        onClick: () => navigate(`/learning-development/survey/${row.surveyId}`),
      },
    ],
    [navigate],
  );

  return (
    <IndexPage<SurveyTrainingRecommendation>
      module="training-recommendations"
      breadcrumb={['Learning & Development', 'Training Recommendations']}
      columns={columns}
      facets={[{ key: 'status', label: 'Status' }]}
      // A recommendation, each employee's track on it, and what they attached to their report.
      auditEntityTypes={[
        'SurveyTrainingRecommendation',
        'SurveyTrainingRecommendationRecipient',
        'SurveyTrainingRecommendationReportAttachment',
      ]}
      rowKey={(row) => row.id}
      fetch={fetchRows}
      searchPlaceholder="Search recommendations, plans and surveys"
      emptyMessage="No training has been recommended yet."
      // The TITLE opens the recommendation's profile — a page, so the breadcrumb carries the way back.
      onRowOpen={(row) => navigate(`/learning-development/training-recommendation/${row.id}`)}
      openPermission="training-recommendations.read"
      rowActions={rowActions}
      selectable
      otherInfo={(row) => row}
    />
  );
}
